import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import api, { formatApiError } from "@/lib/api";

export default function AdminEventSettings() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/event-settings").then(({ data }) => setEv(data)); }, []);

  const set = (k) => (e) => setEv((s) => ({ ...s, [k]: e.target.value }));
  const setList = (k) => (e) => setEv((s) => ({ ...s, [k]: e.target.value.split("\n").filter(Boolean) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/event-settings", ev);
      setEv(data);
      toast.success("Event information updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  if (!ev) return (<div className="min-h-screen bg-[#FAF6F0]"><Navbar /><div className="p-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div></div>);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF6F0]">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#8B1E2B]" data-testid="back-to-queue"><ArrowLeft className="h-4 w-4" /> {t("admin_queue")}</button>
        <h1 className="mt-4 font-serif text-3xl text-[#8B1E2B]" data-testid="event-settings-title">{t("event_settings")}</h1>
        <p className="text-sm text-slate-500 mt-1">This is the editable event information area shown on the public page.</p>

        <div className="mt-6 rounded-2xl bg-white border border-amber-500/20 p-6 shadow-sm space-y-5" data-testid="event-settings-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <F label="Event name (EN)" v={ev.event_name_en} on={set("event_name_en")} id="event_name_en" />
            <F label="कार्यक्रम नाम (HI)" v={ev.event_name_hi} on={set("event_name_hi")} id="event_name_hi" />
            <F label="Event date" v={ev.event_date} on={set("event_date")} id="event_date" type="date" />
            <F label="City (EN)" v={ev.city_en} on={set("city_en")} id="city_en" />
            <F label="शहर (HI)" v={ev.city_hi} on={set("city_hi")} id="city_hi" />
            <F label="Venue (EN)" v={ev.venue_en} on={set("venue_en")} id="venue_en" />
            <F label="स्थान (HI)" v={ev.venue_hi} on={set("venue_hi")} id="venue_hi" />
            <F label="Contact email" v={ev.contact_email} on={set("contact_email")} id="contact_email" type="email" />
            <F label="Contact phone" v={ev.contact_phone} on={set("contact_phone")} id="contact_phone" />
          </div>
          <TA label="Tagline (EN)" v={ev.tagline_en} on={set("tagline_en")} id="tagline_en" />
          <TA label="टैगलाइन (HI)" v={ev.tagline_hi} on={set("tagline_hi")} id="tagline_hi" />
          <TA label="About (EN)" v={ev.about_en} on={set("about_en")} id="about_en" />
          <TA label="परिचय (HI)" v={ev.about_hi} on={set("about_hi")} id="about_hi" />
          <TA label="Eligibility (EN) — one per line" v={(ev.eligibility_en || []).join("\n")} on={setList("eligibility_en")} id="eligibility_en" rows={4} />
          <TA label="पात्रता (HI) — प्रति पंक्ति एक" v={(ev.eligibility_hi || []).join("\n")} on={setList("eligibility_hi")} id="eligibility_hi" rows={4} />

          <Button onClick={save} disabled={saving} className="bg-[#8B1E2B] hover:bg-[#6f1622] text-white" data-testid="event-settings-save-button">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {t("save")}
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function F({ label, v, on, id, type = "text" }) {
  return <div><Label htmlFor={id}>{label}</Label><Input id={id} type={type} value={v || ""} onChange={on} className="mt-1.5" data-testid={`event-${id}`} /></div>;
}
function TA({ label, v, on, id, rows = 3 }) {
  return <div><Label htmlFor={id}>{label}</Label><Textarea id={id} rows={rows} value={v || ""} onChange={on} className="mt-1.5" data-testid={`event-${id}`} /></div>;
}
